/* eslint-disable camelcase */
import moment from "moment";
import ReportHelper from "neon-frontend-utils/src/components/Report/helpers/ReportHelper";
import Logger from "neon-frontend-utils/src/logger";
import translator from "neon-frontend-utils/src/translator";
import Utils from "neon-frontend-utils/src/utils";
import BrandingSettings from "../../../constants/BrandingSettings";
import ProductTypes from "../constants/ProductTypes";
import Components from "./../../../constants/Components";
import * as actionType from "./../constants/ActionTypes";
import NodeTypes from "./../constants/NodeTypes";
import PriceTypes from "./../constants/PriceTypes";

const initialState = {
    activeNode: null,
    addedNode: null,
    availableProducts: [],
    competencies: [],
    competenciesForProduct: [],
    competencyAliases: {},
    competencyAliasTags: {},
    customQuestion: {},
    organisationBranding: {},
    organisationData: {
        generalEmail: null,
        interpretationActive: false,

        reportReceivers: [], // available rr
        sectorId: null,
    },
    organisationIdForCompetencies: null,
    organisationSectors: {
        en_GB: [],
        nl_NL: [],
    },
    parentOrganisationNameForProject: "",
    permissionModuleSettings: null,
    priceConfigForOrganisation: null,
    priceTiers: {},
    productAddons: [],
    products: [],
    project: {
        categoryId: null,
        displayName: "",
        id: null,
        seniority: "",
    },
    projectCategories: {
        en_GB: [],
        nl_NL: [],
    },
    projectProductTemplates: [],
    projectSettings: {},
    selectedId: null,
    selectedProducts: [],
    selfRegistrationConfig: null,
    selfRegistrationTemplateSettings: {
        activeCompetencies: [],
        activeProductAddOns: [],
        assessmentTemplateSettings: {},
        reportReceivers: [],
    },
    textTemplates: {},
    treeNodesV2: {},
    userDetails: null,
    users: [],
};

/**
 * Converts IRI to id
 * @param {string} iri - iri
 * @returns {string} id
 */
function extractIdFromIri(iri) {
    return iri.split("/").pop();
}

/**
 * Returns the new state
 * @param {Object} state - state
 * @param {Object} action - action
 * @returns {{}} new state
 */
export default function organisationSettingsReducer(state = initialState, action) {
    const newState = Object.assign({}, state);

    switch (action.type) {
        case actionType.GET_TREE_NODES_V2: {
            try {
                const sortedTreeNodes = Utils.alphabeticallySortFieldInArray(action.response, "organisationName");

                // clone state (when adding a top level organisation, clear the entire tree)
                newState.treeNodesV2 = action.node ? Object.assign({}, state.treeNodesV2) : {};

                // add received tree nodes to newState
                sortedTreeNodes.forEach((node) => {
                    // for now do not show deactivated organisations
                    if (node.deactivatedOn) {
                        return;
                    }

                    const nodeType = node.type ? NodeTypes[node.type.toUpperCase()] : NodeTypes.ORGANISATION;
                    const path = node.uuid; // no need to construct the path, this is always a single uuid

                    // highlight the child node that was selected
                    let isSelected = action.node && !action.addedNode ? node.id === action.node.id : false;
                    let isOpen = false;

                    if (action.addedNode && path === action.addedNode) {
                        // highlight this node and set it to open, since this is a newly added root node
                        isSelected = true;
                        isOpen = true;

                        // also save to global state
                        newState.selectedId = action.addedNode;
                        newState.addedNode = path; // save the path in localState so it can be cleared when another node is toggled (so no more than 1 node is highlighted)
                    }

                    // construct and add the node to the nodes collection
                    newState.treeNodesV2[path] = {
                        children: [],
                        deactivated: !!node.deactivatedOn,
                        endNode: nodeType === NodeTypes.PROJECT || nodeType === NodeTypes.JOBFUNCTION,

                        id: path,

                        isOpen: isOpen || !!action.node, // when node or addedNode was provided, the path should be open

                        isRoot: true,
                        isSelected,
                        ltpId: node.id,
                        name: node.organisationName || node.name || "",
                        path,
                        type: nodeType,
                        uuid: node.uuid,
                    };

                    // set the new activeNode
                    if (isSelected) {
                        newState.activeNode = newState.treeNodesV2[path];
                    }
                });
            } catch (error) {
                Logger.instance.error({
                    component: Components.ORGANISATION_SETTINGS,
                    error,
                    message: "Exception in organisationSettings reducer GET_TREE_NODES_V2",
                });
            }

            break;
        }

        case actionType.GET_TREE_CHILD_NODES_V2: {
            try {
                // sort the response by name
                const sortedTreeNodes = Utils.alphabeticallySortFieldInArray(action.response, "name");

                // clone state (make sure it is not cleared, these child nodes need to be appended to it)
                newState.treeNodesV2 = Object.assign({}, state.treeNodesV2);

                // add received tree nodes to newState (if applicable, add received nodes as children of the parent on which the new node was added)
                sortedTreeNodes.forEach((node) => {
                    const nodeType =
                        node.type === NodeTypes.ORGANISATION
                            ? NodeTypes.SUBORGANISATION
                            : NodeTypes[node.type.toUpperCase()];
                    const parentNode = newState.treeNodesV2[action.path];
                    const path = `${parentNode.path}/${node.uuid}`; // path to newly received child node

                    let isSelected = false;
                    let isOpen = false;

                    // handle auto-selection of newly added child node
                    if (action.addedNode && path === action.addedNode) {
                        isSelected = true;
                        isOpen = true;

                        // unselect previous node
                        if (newState.selectedId) {
                            Object.keys(newState.treeNodesV2).forEach((previousNode) => {
                                if (newState.treeNodesV2[previousNode].id === state.selectedId) {
                                    newState.treeNodesV2[previousNode].isSelected = false;
                                }
                            });
                        }

                        // also save to global state
                        newState.selectedId = action.addedNode;
                        newState.addedNode = node.uuid; // save the path in localState so it can be cleared when another node is toggled (so no more than 1 node is highlighted)
                    }

                    // populate the children array of the parent node, if not already in it
                    if (!parentNode.children.includes(path)) {
                        parentNode.children.push(path);
                    }

                    // construct and add the node to the nodes collection
                    newState.treeNodesV2[path] = {
                        children: [],
                        deactivated: !!node.deactivatedOn,
                        endNode: nodeType === NodeTypes.PROJECT || nodeType === NodeTypes.JOBFUNCTION,
                        id: path,
                        isOpen,
                        isRoot: false,
                        isSelected,
                        ltpId: node.id,
                        name: node.organisationName || node.name || "",
                        path,
                        selfRegistration: node.selfRegistration || false,
                        type: nodeType,
                        uuid: node.uuid,
                    };

                    // set the new activeNode
                    if (isSelected) {
                        newState.activeNode = newState.treeNodesV2[path];
                    }

                    // also automatically open the parent node of the just-added child node
                    if (action.addedNode) {
                        Object.keys(newState.treeNodesV2).forEach((possibleParentNode) => {
                            if (newState.treeNodesV2[possibleParentNode].id === state.selectedId) {
                                newState.treeNodesV2[possibleParentNode].isOpen = true;
                            }
                        });
                    }
                });
            } catch (error) {
                Logger.instance.error({
                    component: Components.ORGANISATION_SETTINGS,
                    error,
                    message: "Exception in organisationSettings reducer GET_TREE_CHILD_NODES_V2",
                });
            }

            break;
        }

        case actionType.TOGGLE_TREE_NODE_V2: {
            try {
                // clone state
                newState.treeNodesV2 = Object.assign({}, state.treeNodesV2);

                let node;

                // get the clicked node from the cloned state
                Object.keys(newState.treeNodesV2).forEach((clickedNode) => {
                    if (newState.treeNodesV2[clickedNode].id === action.id) {
                        node = newState.treeNodesV2[clickedNode];
                    }
                });

                if (!node) {
                    return new Error("Could not find the clicked node in nodes collection");
                }

                // unselect previous node
                if (newState.selectedId) {
                    Object.keys(newState.treeNodesV2).forEach((previousNode) => {
                        if (newState.treeNodesV2[previousNode].id === state.selectedId) {
                            newState.treeNodesV2[previousNode].isSelected = false;
                        }
                    });
                }

                // select new node
                if (action.id) {
                    node.isSelected = !node.isSelected;
                    node.isOpen = !node.isOpen;

                    // also save it in the global state
                    newState.selectedId = action.id;
                }
            } catch (error) {
                Logger.instance.error({
                    component: Components.ORGANISATION_SETTINGS,
                    error,
                    message: "Exception in organisationSettings reducer TOGGLE_TREE_NODE_V2",
                });
            }

            break;
        }

        case actionType.GET_USERS: {
            try {
                newState.users = action.response.accounts;
            } catch (error) {
                Logger.instance.error({
                    component: Components.ORGANISATION_SETTINGS,
                    error,
                    message: "Exception in organisationSettings reducer GET_USERS",
                });
            }

            break;
        }

        case actionType.GET_PRODUCTS: {
            try {
                // clear current items from newState
                newState.products = [];
                newState.productAddons = [];
                newState.selectedProducts = [];

                // loop through newly retrieved items from the action and add to the newState
                action.response.products.forEach((product) => {
                    // NEON-10410: prevent phased products appearing twice
                    if (!newState.products.some((prod) => prod.id === product.id)) {
                        newState.products.push({
                            autoEvaluation: product.autoEvaluation,
                            description: product.description,
                            id: product.id,
                            name: product.translationKey,
                            uuid: product.uuid,
                        });

                        if (product.active) {
                            newState.selectedProducts.push(product.uuid);
                        }
                    }
                });

                action.response.productAddOns.forEach((productAddon) => {
                    let disabled = true;

                    // check if the add on should be enabled for this organisation
                    for (let i = 0; i < productAddon.addOnAvailableForProducts.length; i++) {
                        if (newState.selectedProducts.includes(productAddon.addOnAvailableForProducts[i])) {
                            disabled = false;
                            break;
                        }
                    }

                    newState.productAddons.push({
                        available: productAddon.availableForOrganisation,
                        availableForProducts: productAddon.addOnAvailableForProducts,
                        disabled,
                        name: productAddon.translationKey,
                        productAddOnHandle: productAddon.productAddOnHandle,
                        slug: productAddon.slug,
                        translationKey: productAddon.translationKey,
                        type: productAddon.type,
                    });
                });
            } catch (error) {
                Logger.instance.error({
                    component: Components.ORGANISATION_SETTINGS,
                    error,
                    message: "Exception in organisationSettings reducer GET_PRODUCTS",
                });
            }

            break;
        }

        case actionType.GET_AVAILABLE_PRODUCTS: {
            try {
                // clear current items from newState
                newState.availableProducts = [];

                // loop through newly retrieved items from the action and add to the newState
                action.products.forEach((product) => {
                    const availableProduct = {
                        alias: {},
                        autoEvaluation: product.autoEvaluation,
                        availableAssessmentGoals: product.availableAssessmentGoals,
                        availableInterpretations: product.availableInterpretations,
                        availableProductAddons: product.availableProductAddons,
                        capTestDomains: product.capTestDomains || null,
                        captestDomainsMandatory: product.captestDomainsMandatory,
                        captestDomainsSelectable: product.captestDomainsSelectable,
                        description: product.description,
                        hasMboLevels: product.hasMboLevels,
                        id: product.id,
                        isDevelopmentProduct: product.isDevelopmentProduct,
                        isOnlineProduct: product.isOnlineProduct,
                        name: product.translationKey,
                        productHandle: product.productHandle,
                        skipCompetencySelection: product.skipCompetencySelection,
                        toggleAbilityTests: product.toggleAbilityTests,
                        uuid: product.uuid,
                        withAbilityTests: Object.values(product.capTestDomains || {}).some((v) => v),
                    };

                    if (action.productAliases) {
                        const productAlias = action.productAliases.find((alias) => alias.product.id === product.id);

                        if (productAlias) {
                            availableProduct.alias = {
                                id: productAlias.id,
                                name: productAlias.name,
                                productId: productAlias.product.id,
                            };
                        }
                    }
                    newState.availableProducts.push(availableProduct);
                });
            } catch (error) {
                Logger.instance.error({
                    component: Components.ORGANISATION_SETTINGS,
                    error,
                    message: "Exception in organisationSettings reducer GET_AVAILABLE_PRODUCTS",
                });
            }

            break;
        }

        case actionType.SET_PRODUCT_ADDON_AVAILABILITY: {
            try {
                const productAddons = [...newState.productAddons];

                for (let i = 0; i < productAddons.length; i++) {
                    if (productAddons[i].slug === action.productAddonSlug) {
                        productAddons[i].available = action.available;
                        break;
                    }
                }

                newState.productAddons = productAddons;
            } catch (error) {
                Logger.instance.error({
                    component: Components.ORGANISATION_SETTINGS,
                    error,
                    message: "Exception in organisationSettings reducer SET_PRODUCT_ADDON_AVAILABILITY",
                });
            }
            break;
        }

        case actionType.TOGGLE_PRODUCT: {
            try {
                const newSelectedProducts = [];

                if (action.multipleAllowed) {
                    newState.selectedProducts.forEach((selectedProduct) => {
                        // if it was there, dont push it to the 'new' selection (deselect)
                        if (selectedProduct !== action.uuid) {
                            newSelectedProducts.push(selectedProduct);
                        }
                    });

                    // if it was not there before, push it to the 'new' selection (select)
                    if (!newState.selectedProducts.includes(action.uuid)) {
                        newSelectedProducts.push(action.uuid);
                    }

                    newState.selectedProducts = newSelectedProducts;
                } else {
                    newSelectedProducts.push(action.uuid);
                }
            } catch (error) {
                Logger.instance.error({
                    component: Components.ORGANISATION_SETTINGS,
                    error,
                    message: "Exception in organisationSettings reducer TOGGLE_PRODUCT",
                });
            }
            break;
        }

        case actionType.GET_COMPETENCIES: {
            try {
                newState.competencies = [];
                newState.organisationIdForCompetencies = null;

                const newCompetencies = [];

                Object.entries(action.response).forEach(([key, competency]) => {
                    const competencyLabel =
                        ReportHelper.getCompetencyName(competency, action.languageId, action.i18n) ||
                        competency.competencyName;
                    const competencyDefinition =
                        ReportHelper.getCompetencyDefinition(competency, action.languageId) ||
                        competency.competencyDefinition;

                    const constructedCompetency = {
                        aliases: competency.aliases,
                        definitionText: competencyDefinition,
                        disabled: false,
                        group: competency.competencyGroup,
                        icon: `icon_${competency.translationKey}`,
                        id: competency.id,
                        isGlobalCompetency: competency.isGlobalCompetency,
                        isLinkedToAbilityTest: competency.isLinkedToAbilityTest,
                        isMboCompetency: competency.isMboCompetency,
                        isOnlineCompetency: competency.isOnlineCompetency,
                        key,
                        label: competencyLabel,
                        selected: competency.isActive,
                        translationKey: competency.translationKey,
                    };

                    // push all competencies to array of value options
                    newCompetencies.push(constructedCompetency);
                });

                newState.competencies = newCompetencies;
                newState.organisationIdForCompetencies = action.organisationId;
            } catch (error) {
                Logger.instance.error({
                    component: Components.ORGANISATION_SETTINGS,
                    error,
                    message: "Exception in organisationSettings reducer GET_COMPETENCIES",
                });
            }
            break;
        }

        case actionType.GET_COMPETENCIES_FOR_PRODUCT: {
            try {
                newState.competenciesForProduct = [];
                newState.organisationIdForCompetencies = null;
                const newCompetencies = [];

                Object.entries(action.response).forEach(([key, competency]) => {
                    const competencyLabel =
                        ReportHelper.getCompetencyName(competency, action.languageId, action.i18n) ||
                        competency.competencyName;
                    const competencyDefinition =
                        ReportHelper.getCompetencyDefinition(competency, action.languageId, action.i18n) ||
                        competency.competencyDefinition;
                    const constructedCompetency = {
                        aliases: competency.aliases,
                        competencyDefinition: competency.competencyDefinition,
                        competencyName: competency.competencyName,
                        definitionText: competencyDefinition,
                        disabled: false,
                        group: competency.competencyGroup,
                        icon: `icon_${competency.translationKey}`,
                        id: competency.id,
                        isGlobalCompetency: competency.isGlobalCompetency,
                        isLinkedToAbilityTest: competency.isLinkedToAbilityTest,
                        isMboCompetency: competency.isMboCompetency,
                        isOnlineCompetency: competency.isOnlineCompetency,
                        key,
                        label: competencyLabel,
                        selected: competency.isActive,
                        translationKey: competency.translationKey,
                    };

                    // push all competencies to array of value options
                    newCompetencies.push(constructedCompetency);
                });

                newState.competenciesForProduct = newCompetencies;
                newState.organisationIdForCompetencies = action.organisationId;
            } catch (error) {
                Logger.instance.error({
                    component: Components.ORGANISATION_SETTINGS,
                    error,
                    message: "Exception in organisationSettings reducer GET_COMPETENCIES_FOR_PRODUCT",
                });
            }
            break;
        }

        case actionType.GET_COMPETENCY_ALIAS_DATA: {
            try {
                newState.competencyAliases = {};

                Object.keys(action.response.competencyAliases).forEach((languageId) => {
                    const i18n = translator(languageId, ["competencies"]);

                    const competency = newState.competencies?.find?.((c) => c.key === action.response.uuid);

                    const defaultName = i18n[`competencies_${competency?.translationKey}`] || "";
                    const defaultDefinition = i18n[`competencies_${competency?.translationKey}_definition`] || "";

                    newState.competencyAliases[languageId] = {
                        aliasId: action.response.competencyAliases[languageId].id,
                        aliasSlug: action.response.competencyAliases[languageId].uuid,
                        competencyAliasDefinition: action.response.competencyAliases[languageId].definition || "",
                        competencyAliasName: action.response.competencyAliases[languageId].name || "",
                        competencyAliasTag: action.response.competencyAliases[languageId].tag,
                        competencySlug: action.response.uuid,
                        defaultDefinition,
                        defaultName,
                        id: action.response.id,
                        translationKey: competency.translationKey,
                    };
                });
            } catch (error) {
                Logger.instance.error({
                    component: Components.ORGANISATION_SETTINGS,
                    error,
                    message: "Exception in organisationSettings reducer GET_COMPETENCY_ALIAS_DATA",
                });
            }

            break;
        }

        case actionType.GET_COMPETENCY_ALIAS_TAGS: {
            try {
                newState.competencyAliasTags = action.response;
            } catch (error) {
                Logger.instance.error({
                    component: Components.ORGANISATION_SETTINGS,
                    error,
                    message: "Exception in organisationSettings reducer GET_COMPETENCY_ALIAS_TAGS",
                });
            }

            break;
        }

        case actionType.GET_USER_DETAILS: {
            try {
                newState.userDetails = {
                    displayName: action.response.displayName || "",
                    email: action.response.email || "",
                    firstName: action.response.firstName || "",
                    gender: action.response.gender || "",
                    infix: action.response.infix || "",
                    lastName: action.response.lastName || "",
                };
            } catch (error) {
                Logger.instance.error({
                    component: Components.ORGANISATION_SETTINGS,
                    error,
                    message: "Exception in organisationSettings reducer GET_USER_DETAILS",
                });
            }

            break;
        }

        case actionType.UPDATE_AMENDED_ENTITY: {
            try {
                newState.treeNodesV2 = Object.assign({}, state.treeNodesV2);
                newState.treeNodesV2[state.selectedId].name = action.value;
            } catch (error) {
                Logger.instance.error({
                    component: Components.ORGANISATION_SETTINGS,
                    error,
                    message: "Exception in organisationSettings reducer UPDATE_AMENDED_ENTITY",
                });
            }

            break;
        }

        case actionType.GET_ORGANISATION_BRANDING: {
            try {
                if (action.organisationBranding && action.organisationSlug && action.organisationBranding.branding) {
                    let imageUrl =
                        action.organisationBranding.branding.logo || BrandingSettings.organisationLogo.placeholder;

                    // add cache buster, to ensure reloading of images when state updates
                    imageUrl += `?${Date.now()}`;

                    newState.organisationBranding = {
                        ...newState.organisationBranding,
                        [action.organisationSlug]: {
                            ...newState.organisationBranding[action.organisationSlug],
                            logoUrl: imageUrl,
                        },
                    };
                }
            } catch (error) {
                Logger.instance.error({
                    component: Components.ORGANISATION_SETTINGS,
                    error,
                    message: "Exception in organisationSettings reducer GET_ORGANISATION_BRANDING",
                });
            }

            break;
        }

        case actionType.GET_TEMPLATES: {
            try {
                Object.keys(action.response).forEach((key) => {
                    if (key === action.productName) {
                        newState.textTemplates = action.response[key];
                    }
                });
            } catch (error) {
                Logger.instance.error({
                    component: Components.ORGANISATION_SETTINGS,
                    error,
                    message: "Exception in organisationSettings reducer GET_TEMPLATES",
                });
            }

            break;
        }

        case actionType.TOGGLE_IS_INTERPRETATION_ACTIVE: {
            try {
                newState.organisationData.interpretationActive = action.interpretationActive;
            } catch (error) {
                Logger.instance.error({
                    component: Components.ASSESSMENT_TYPE_LIST,
                    error,
                    message: "Exception in organisationSettings reducer TOGGLE_IS_INTERPRETATION_ACTIVE",
                });
            }
            break;
        }

        case actionType.GET_PRICE_CONFIG_FOR_ORGANISATION: {
            try {
                newState.priceConfigForOrganisation = initialState.priceConfigForOrganisation;

                const products = action.response[0];
                const tiers = action.response[1];
                const licenses = action.response[2];
                const addOnTiers = action.response[3];
                const priceConfigPerProduct = {};

                products.products.forEach((product) => {
                    if (product.active) {
                        let type = PriceTypes.TIER; // by default, it falls back to global tiers
                        let id = null; // null means no organisation -fixed price or -license was set
                        const productId = product.id; // id of global tiers
                        let price = null;
                        let start = null;
                        let end = null;
                        const productType = ProductTypes.PRODUCT;

                        const name = product.translationKey;
                        const productTier = tiers.filter(
                            (tier) =>
                                parseInt(tier.product.id, 0) === parseInt(product.id, 0) &&
                                tier.phase === product.phase,
                        );
                        const productLicense = licenses.filter(
                            (license) => parseInt(extractIdFromIri(license.product), 0) === parseInt(product.id, 0),
                        );

                        if (productTier && productTier.length && productTier[0]) {
                            type = PriceTypes.UNIT;
                            id = productTier[0].id;
                            price = productTier[0].price / 100; // price comes in as cents and should be shown in euro's
                        }

                        if (productLicense && productLicense.length && productLicense[0]) {
                            type = PriceTypes.LICENSE;
                            id = productLicense[0].id;
                            start = productLicense[0].start
                                ? moment(productLicense[0].start, "YYYY-MM-DD").format("YYYY/MM/DD")
                                : null;
                            end = productLicense[0].end
                                ? moment(productLicense[0].end, "YYYY-MM-DD").format("YYYY/MM/DD")
                                : null;
                        }

                        const key = product.phase ? `${product.uuid}_${product.phase}` : product.uuid;

                        priceConfigPerProduct[key] = {
                            end,
                            id,
                            name,
                            phase: product.phase,
                            price,
                            productId,
                            productType,
                            productUuid: product.uuid,
                            start,
                            type,
                        };
                    }
                });

                products.productAddOns.forEach((addOn) => {
                    if (
                        addOn.availableForOrganisation &&
                        (newState.organisationData.interpretationActive || addOn.type !== "interpretation")
                    ) {
                        let type = PriceTypes.TIER; // by default, it falls back to global tiers
                        let id = null; // null means no organisation -fixed price was set
                        const productId = addOn.id; // id of global tiers
                        let price = null;
                        const productType = ProductTypes.ADDON;
                        const addOnType = addOn.type;
                        const name = addOn.translationKey;
                        const addOnTier = addOnTiers.filter(
                            (tier) => parseInt(tier.productAddOn.id, 0) === parseInt(addOn.id, 0),
                        );

                        if (addOnTier && addOnTier.length && addOnTier[0]) {
                            type = PriceTypes.UNIT;
                            id = addOnTier[0].id;
                            price = addOnTier[0].price / 100; // price comes in as cents and should be shown in euro's
                        }

                        priceConfigPerProduct[addOn.slug] = {
                            addOnType,
                            id,
                            name,
                            price,
                            productId,
                            productType,
                            type,
                        };
                    }
                });

                newState.priceConfigForOrganisation = priceConfigPerProduct;
            } catch (error) {
                Logger.instance.error({
                    component: Components.ORGANISATION_SETTINGS,
                    error,
                    message: "Exception in organisationSettings reducer GET_PRICE_CONFIG_FOR_ORGANISATION",
                });
            }

            break;
        }

        case actionType.GET_PROJECT_INFO: {
            newState.project = initialState.project;
            newState.project.id = action.response.id;
            newState.project.categoryId = action.response.professionalCategoryId;
            newState.project.seniority = action.response.seniority;
            newState.project.displayName = action.response.displayName;
            break;
        }

        // note: this is not coming from a call to projectSettings, it fetches the settings for a project
        case actionType.GET_PROJECT_SETTINGS: {
            try {
                newState.projectSettings = initialState.projectSettings;

                if (action.response.organisation) {
                    newState.parentOrganisationNameForProject = action.response.organisation.organisationName;
                    const newProductAddons = [];
                    const products = action.response.organisation.products;

                    // which add-ons are activated on organisation level?
                    const availableAddOns = action.response.organisation.availableProductAddOns;

                    availableAddOns.forEach((addon) => {
                        const availableForProducts = [];

                        products.forEach((product) => {
                            // iterate each product, inspect the available add-ons for it, and see if there is a match on uuid, then add it
                            if (product.availableProductAddons) {
                                const productThatSupportsThisAddOn = product.availableProductAddons.filter(
                                    (ao) => ao.uuid === addon.uuid,
                                );

                                if (productThatSupportsThisAddOn && productThatSupportsThisAddOn[0]) {
                                    availableForProducts.push(product.uuid);
                                }
                            }
                        });

                        newProductAddons.push({
                            available: true,
                            availableForProducts,
                            disabled: false,
                            id: addon.id,
                            name: addon.addOnName,
                            slug: addon.uuid,
                            translationKey: addon.translationKey,
                        });
                    });

                    newState.productAddons = newProductAddons;
                }

                // the following extractions are needed in various places, replicating the old 'real' projectSettings flow a bit

                if (action.response.id) {
                    newState.projectSettings.id = action.response.id;
                }

                if (action.response.hasOwnProperty("phaseForwarding")) {
                    newState.projectSettings.phaseForwarding = action.response.phaseForwarding;
                }

                if (action.response.hasOwnProperty("fileTenantAwares")) {
                    newState.projectSettings.fileTenantAwares = action.response.fileTenantAwares;
                }

                if (action.response.hasOwnProperty("simulationType")) {
                    newState.projectSettings.simulationType = action.response.simulationType;
                }

                if (action.response.hasOwnProperty("authProviders")) {
                    newState.projectSettings.authProviders = action.response.authProviders;
                }

                if (action.response.organisation?.availableInterpretations) {
                    newState.projectSettings.availableInterpretations =
                        action.response.organisation.availableInterpretations;
                }

                if (action.response.hasOwnProperty("selfRegistration")) {
                    newState.projectSettings.selfRegistration = action.response.selfRegistration;

                    // sr projects have the product stored in selfRegistrationTemplates (slug is also required, since eg. product picker depends on it)
                    if (action.response?.selfRegistrationTemplates[0]?.product) {
                        newState.projectSettings.productSlug =
                            action.response.selfRegistrationTemplates[0].product.uuid;
                        newState.projectSettings.product = action.response.product =
                            action.response.selfRegistrationTemplates[0].product.id;
                    }

                    // extracting props from assessmentTemplateSettings to projectSettings (only SR)
                    if (action.response?.selfRegistrationTemplates[0]?.assessmentTemplateSettings) {
                        const ignoredSettingHandles = ["id"];

                        const newObject =
                            action.response.selfRegistrationTemplates[0].assessmentTemplateSettings.reduce(
                                (acc, obj) => {
                                    // be careful not to include keys that interfere with previous extractions (like project id!)
                                    if (!ignoredSettingHandles.includes(obj.settingHandle)) {
                                        acc[obj.settingHandle] = obj.value;
                                    }

                                    return acc;
                                },
                                {},
                            );

                        newState.projectSettings = {
                            ...newState.projectSettings,
                            ...newObject,
                        };
                    }
                }

                // normal projects have the product stored in the root
                if (action.response.product) {
                    newState.projectSettings.product = action.response.product;
                }

                if (action.response.displayName) {
                    newState.project.displayName = action.response.displayName;
                }

                if (action.response.hasOwnProperty("mandatoryReflectionEnabled")) {
                    newState.projectSettings.mandatoryReflectionEnabled = action.response.mandatoryReflectionEnabled;
                }
            } catch (error) {
                Logger.instance.error({
                    component: Components.ORGANISATION_SETTINGS,
                    error,
                    message: "Exception in organisationSettings reducer GET_PROJECT_SETTINGS",
                });
            }

            break;
        }

        case actionType.GET_ORGANISATION_DATA: {
            try {
                newState.organisationData = {};
                newState.organisationData.reportReceivers = [];

                if (action.response.organisationReportReceivers && action.response.organisationReportReceivers.length) {
                    action.response.organisationReportReceivers.forEach((reportReceiverObj, index) => {
                        const reportReceiver = reportReceiverObj.accountHasRole.account;
                        const infix = reportReceiver.infix ? ` ${reportReceiver.infix} ` : " ";
                        const reportReceiverName = `${reportReceiver.firstName}${infix}${reportReceiver.lastName}`;

                        newState.organisationData.reportReceivers.push({
                            email: reportReceiver.email,
                            id: reportReceiverObj.id,
                            label: reportReceiverName,
                            selected: reportReceiver.selected || false,
                            slug: reportReceiver.uuid,
                            uniqueId: index,
                            value: reportReceiver.uuid,
                        });
                    });
                }

                newState.organisationData.sectorId = action.response.sectorId;
                newState.organisationData.generalEmail = action.response.generalEmail;
                newState.organisationData.authProviders = action.response.authProviders;
                newState.organisationData.interpretationActive = action.response.interpretationActive;
                newState.organisationData.anonymizeNonHiredAfterDays = action.response.anonymizeNonHiredAfterDays;
                newState.organisationData.autoUpdateOutcomeDays = action.response.autoUpdateOutcomeDays;
            } catch (error) {
                Logger.instance.error({
                    component: Components.ORGANISATION_SETTINGS,
                    error,
                    message: "Exception in organisationSettings reducer GET_ORGANISATION_DATA",
                });
            }

            break;
        }

        case actionType.GET_CUSTOM_QUESTION: {
            try {
                newState.customQuestion = initialState.customQuestion;
                newState.customQuestion = action.response;
            } catch (error) {
                Logger.instance.error({
                    component: Components.ORGANISATION_SETTINGS,
                    error,
                    message: "Exception in organisationSettings reducer GET_CUSTOM_QUESTION",
                });
            }
            break;
        }

        case actionType.GET_PROJECT_PRODUCT_TEMPLATES: {
            newState.projectProductTemplates = Object.values(action.response).map((item) => ({
                ...item,
            }));

            break;
        }

        case actionType.GET_ORGANISATION_SECTORS: {
            newState.organisationSectors[action.language] = action.response;

            break;
        }

        case actionType.GET_SELF_REGISTRATION_TEMPLATE_SETTINGS: {
            try {
                newState.selfRegistrationTemplateSettings = {
                    activeCompetencies: [],
                    activeProductAddOns: [],
                    assessmentTemplateSettings: {},
                    reportReceivers: [],
                };

                // this holds the settings for a project (there is no relation with the refactored BE endpoint with the same name)
                newState.projectSettings = initialState.projectSettings;

                // add SR productAddOns (if they exist)
                if (action.response?.selfRegistrationTemplates[0]?.activeProductAddOns) {
                    newState.selfRegistrationTemplateSettings.activeProductAddOns =
                        action.response.selfRegistrationTemplates[0].activeProductAddOns;
                }

                // add SR reportReceivers (if they exist)
                if (action.response?.selfRegistrationTemplates[0]?.reportReceivers) {
                    newState.selfRegistrationTemplateSettings.reportReceivers =
                        action.response.selfRegistrationTemplates[0].reportReceivers;
                }

                // add product id
                if (action.response?.selfRegistrationTemplates[0]?.product?.id) {
                    newState.selfRegistrationTemplateSettings.product =
                        action.response.selfRegistrationTemplates[0].product.id;
                }

                // add project id
                if (action.response.id) {
                    newState.selfRegistrationTemplateSettings.project = action.response.id;
                }

                if (action.response?.selfRegistrationTemplates[0]?.assessmentTemplateSettings) {
                    const tempSettingsObj = {
                        customQuestionId:
                            action.response.selfRegistrationTemplates[0].customQuestion?.id?.toString() || null,
                        id: action.response.selfRegistrationTemplates[0].id?.toString(),
                        uuid: action.response.selfRegistrationTemplates[0].uuid,
                    };

                    action.response.selfRegistrationTemplates[0].assessmentTemplateSettings.forEach((setting) => {
                        tempSettingsObj[setting.settingHandle] = setting.value;
                    });

                    newState.selfRegistrationTemplateSettings.assessmentTemplateSettings = tempSettingsObj;
                }

                newState.selfRegistrationTemplateSettings.selfRegistration = action.response.selfRegistration || false;
            } catch (error) {
                Logger.instance.error({
                    component: Components.ORGANISATION_SETTINGS,
                    error,
                    message: "Exception in organisationSettings reducer GET_SELF_REGISTRATION_TEMPLATE_SETTINGS",
                });
            }
            break;
        }

        case actionType.GET_PROFILE_CATEGORIES: {
            newState.projectCategories[action.language] = action.response;

            break;
        }

        case actionType.GET_PERMISSION_MODULE_SETTINGS: {
            newState.permissionModuleSettings = action.response.permissionModuleSettings;
            break;
        }

        case actionType.CLEAR_TREE_NODES: {
            newState.treeNodesV2 = initialState.treeNodesV2;

            break;
        }

        case actionType.CLEAR_USERS: {
            newState.users = initialState.users;

            break;
        }

        case actionType.CLEAR_COMPETENCIES: {
            newState.competencies = initialState.competencies;
            newState.competenciesForProduct = initialState.competenciesForProduct;
            newState.organisationIdForCompetencies = initialState.organisationIdForCompetencies;

            break;
        }

        case actionType.CLEAR_PRODUCTS: {
            newState.products = initialState.products;
            newState.selectedProducts = initialState.selectedProducts;
            newState.availableProducts = initialState.availableProducts;

            break;
        }

        case actionType.CLEAR_USER_DETAILS: {
            newState.userDetails = initialState.userDetails;

            break;
        }

        case actionType.CLEAR_COMPETENCY_ALIAS_DATA: {
            newState.competencyAliases = initialState.competencyAliases;

            break;
        }

        case actionType.CLEAR_TEMPLATES: {
            newState.textTemplates = initialState.textTemplates;

            break;
        }

        case actionType.CLEAR_PROJECT_INFO: {
            newState.project = {
                categoryId: null,
                displayName: "",
                id: null,
            };

            break;
        }

        case actionType.CLEAR_PROJECT_SETTINGS: {
            newState.projectSettings = initialState.projectSettings;
            newState.parentOrganisationNameForProject = initialState.parentOrganisationNameForProject;
            newState.project = {
                categoryId: null,
                displayName: "",
                id: null,
                seniority: "",
            };

            break;
        }

        case actionType.CLEAR_CUSTOM_QUESTION: {
            newState.customQuestion = initialState.customQuestion;

            break;
        }

        case actionType.CLEAR_PROJECT_PRODUCT_TEMPLATES: {
            newState.projectProductTemplates = initialState.projectProductTemplates;

            break;
        }

        case actionType.CLEAR_ORGANISATION_DATA: {
            newState.organisationData = initialState.organisationData;

            break;
        }

        case actionType.CLEAR_SELF_REGISTRATION_TEMPLATE_SETTINGS: {
            newState.selfRegistrationTemplateSettings = {
                activeCompetencies: [],
                activeProductAddOns: [],
                assessmentTemplateSettings: {},
                reportReceivers: [],
            };

            break;
        }

        case actionType.CLEAR_PRICE_CONFIG_FOR_ORGANISATION: {
            newState.priceConfigForOrganisation = initialState.priceConfigForOrganisation;

            break;
        }

        case actionType.CLEAR_PERMISSION_MODULE_SETTINGS: {
            newState.permissionModuleSettings = initialState.permissionModuleSettings;

            break;
        }

        default:
            break;
    }

    return newState;
}
